import { ThunkDispatch } from "@reduxjs/toolkit";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  resetStateShareSite,
  setSelectedPartner,
  setSelectedSort,
  setSelectedStoragesIds,
  setSelectSpecieFilter,
  setSelectTypeOfFilter,
  setStorages,
  setSuccessShareDeleteBins,
  setToDelete,
  setToKeep,
} from "../../../redux/slices/shareSite/shareSite";

import { State } from "../../../redux/slices";

import {
  fetchingSharingStatus,
  fetchListOfPartnerOrganisations,
  fetchStoragesOfEachLocationLocationView,
  listSpecies,
  multiStorageSharing,
  typeOfBin,
} from "../../../redux/actions/shareSite/shareSiteAsync";

import SelectPartnerCard from "./partsOfShareSite/ShareSitePartnerSelectCard";
import ShareSiteStoragesSelection from "./partsOfShareSite/ShareSiteStoragesSelection";
import ShareSiteMenu from "./partsOfShareSite/ShareSiteMenu";
import ShareSiteFooter from "./partsOfShareSite/ShareSiteFooter";
import ShareSiteShowErrors from "./partsOfShareSite/ShareSiteShowErrors";
import { useTranslation } from "react-i18next";
import {
  ListOfSpeciesData,
  ListTypeOfData,
  StoragesData,
} from "../../../redux/slices/shareSite/shareSiteTypes";
import { CheckExpiredToken } from "../../../utils/authentication";
import { SetActionsLayout } from "../../layout/Layout";

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  return {
    resetShareSiteStatement: () => {
      dispatch(resetStateShareSite());
    },

    setSuccessShareDeleteBins: () => {
      dispatch(setSuccessShareDeleteBins(false));
    },

    fetchListOfPartnerOrganisations: () => {
      dispatch(fetchListOfPartnerOrganisations(null));
    },

    setSelectedPartner: (selectedPatnerId: string) => {
      dispatch(setSelectedPartner(selectedPatnerId));
    },

    fetchStoragesOfEachLocationLocationView: (
      orgGeoPointId: string,
      selectedPatner: number,
    ) => {
      dispatch(fetchStoragesOfEachLocationLocationView({ orgGeoPointId }));
      dispatch(fetchingSharingStatus({ orgGeoPointId, selectedPatner }));
    },

    setSelectedStoragesIds: (values: string[]) => {
      dispatch(setSelectedStoragesIds(values));
    },

    fetchFilterValues: () => {
      dispatch(listSpecies(null));
      dispatch(typeOfBin(null));
    },

    setSelectSpecieFilter: (value: number) => {
      dispatch(setSelectSpecieFilter(value));
    },

    setSelectTypeOfFilter: (value: string) => {
      dispatch(setSelectTypeOfFilter(value));
    },

    multiStorageSharing: (
      selectedStoragesToNotShare: string[],
      selectedStoragesToShare: string[],
      theCorrectIdsToDeleteSharedStorage: string[],
    ) => {
      dispatch(
        multiStorageSharing({
          selectedStoragesToNotShare,
          selectedStoragesToShare,
          theCorrectIdsToDeleteSharedStorage,
        }),
      );
    },

    setToKeep: (values: string[]) => {
      dispatch(setToKeep(values));
    },

    setToDelete: (values: string[]) => {
      dispatch(setToDelete(values));
    },

    setStorages: (values: StoragesData) => {
      dispatch(setStorages(values));
    },
    setSelectedSort: (values: string) => {
      dispatch(setSelectedSort(values));
    },
  };
};

const ShareSite: FC = () => {
  const [t] = useTranslation("global");
  const navigate = useNavigate();
  const {
    loadingFetchPartnerOrganisations,
    selectedPatner,
    storages,
    selectedStoragesIds,
    errorFetchPartnerOrganisations,
    storagesError,
    sharingstatusError,
    listOfSpeciesFilter,
    selectedSpecie,
    storagesLoading,
    selectedType,
    listOfTypesFilter,
    listOfPartnerOrganisations,
    toDelete,
    toKeep,
    sharingStatus,
    listOfSpeciesLoading,
    storageSharedDelete,
    storageSharing,
    listTypeOfLoading,
    successShareDeleteBins,
    selectedSort,
  } = useSelector((state: State) => state.ShareSiteReducer);

  const { locationSelectedDetails } = useSelector(
    (state: State) => state.SingleLocationReducer,
  );

  const { orgId, user } = useSelector((state: State) => state.Authentication);

  const {
    fetchListOfPartnerOrganisations,
    resetShareSiteStatement,
    setSelectedPartner,
    fetchStoragesOfEachLocationLocationView,
    setSelectedStoragesIds,
    fetchFilterValues,
    setSelectSpecieFilter,
    setSelectTypeOfFilter,
    multiStorageSharing,
    setToDelete,
    setToKeep,
    setSelectedSort,
    setStorages,
  } = SetActions();
  const { logoutClickHandler } = SetActionsLayout();

  const [run, setRun] = useState(false);
  const noSpecieSelected = 0;
  const infiniteId = 999 * 100;
  const addAllSelected = [
    {
      id: infiniteId,
      slug: "undefined",
      textId: {
        textSlug: "undefined",
        text: t("Share_site.all-species"),
        locale: {
          createdAt: "null",
          updatedAt: "null",
          id: infiniteId,
          value: "null",
          originTableName: "null",
          createdBy: "null",
        },
      },
      conversionFromWheatTons: null,
      textIdLocale: t("Share_site.all-species"),
    },
  ] as unknown as ListOfSpeciesData[];

  const addAllTypeSelected = [
    {
      id: infiniteId.toString(),
      slug: "undefined",
      nameTextId: {
        locale: "null",
        textSlug: "storage-type-name",
        text: t("Share_site.all-storage-types"),
      },
    },
  ] as unknown as ListTypeOfData[];

  const transformlistOfSpeciesFilter = addAllSelected.concat(
    listOfSpeciesFilter.data,
  );

  const transformListTypeOf = addAllTypeSelected.concat(listOfTypesFilter.data);

  useEffect(() => {
    if (selectedSpecie === noSpecieSelected) {
      setTimeout(() => {
        setRun(true);
        setSelectSpecieFilter(transformlistOfSpeciesFilter[0].id);
        setSelectTypeOfFilter(transformListTypeOf[0].id);
        setTimeout(() => {
          setRun(false);
        }, 1000);
      }, 3000);
    } else {
      return () => {};
    }
  }, [run]);

  useEffect(() => {
    fetchListOfPartnerOrganisations();
    fetchFilterValues();
    CheckExpiredToken(logoutClickHandler, user);
  }, []);

  if (successShareDeleteBins) {
    navigate(`/site/details/${locationSelectedDetails?.name}`);
  }

  if (locationSelectedDetails?.id) {
    return (
      <div>
        <div className="share-site-container">
          <ShareSiteMenu
            siteName={locationSelectedDetails?.name}
            loadingFetchPartnerOrganisations={loadingFetchPartnerOrganisations}
            navigate={navigate}
            resetShareSiteStatement={resetShareSiteStatement}
          />

          <SelectPartnerCard
            selectedPatner={selectedPatner}
            listOfPartnerOrganisations={listOfPartnerOrganisations}
            setSelectedPartner={setSelectedPartner}
            fetchStoragesOfEachLocationLocationView={
              fetchStoragesOfEachLocationLocationView
            }
            selectedSiteId={locationSelectedDetails.id as any}
            currentOrgId={orgId ?? 0}
          />

          <ShareSiteShowErrors
            errorFetchPartnerOrganisations={errorFetchPartnerOrganisations}
            storagesError={storagesError}
            sharingstatusError={sharingstatusError}
            listOfPartnerOrganisations={listOfPartnerOrganisations}
          />

          {selectedPatner !== "" ? (
            <ShareSiteStoragesSelection
              storages={storages.data}
              selectedStoragesIds={selectedStoragesIds}
              setSelectedStoragesIds={setSelectedStoragesIds}
              setSelectSpecieFilter={setSelectSpecieFilter}
              selectedSpecie={selectedSpecie}
              listOfSpeciesFilter={transformlistOfSpeciesFilter}
              storagesLoading={storagesLoading}
              noSpecieSelected={noSpecieSelected}
              setSelectTypeOfFilter={setSelectTypeOfFilter}
              typeSelected={selectedType}
              listTypeOfFilter={transformListTypeOf}
              infiniteId={infiniteId}
              setToKeep={setToKeep}
              setToDelete={setToDelete}
              toDelete={toDelete}
              toKeep={toKeep}
              sharingStatus={sharingStatus}
              listOfSpeciesLoading={listOfSpeciesLoading}
              storageSharedDeleteLoading={storageSharedDelete.loading}
              storageSharingLoading={storageSharing.loading}
              listTypeOfLoading={listTypeOfLoading}
              selectedSort={selectedSort}
              setSelectedSort={setSelectedSort}
              setStorages={setStorages}
              storagesData={storages}
            />
          ) : (
            <div />
          )}
        </div>
        <ShareSiteFooter
          selectedStoragesIds={selectedStoragesIds}
          multiStorageSharing={multiStorageSharing}
          toDelete={toDelete}
          sharingStatus={sharingStatus}
        />
      </div>
    );
  } else {
    navigate(`/site/details/${locationSelectedDetails?.name}`);
  }

  return "";
};

export default ShareSite;
